import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";
import {
  AlertTriangle,
  Clock,
  Flame,
  History,
  Inbox,
  Layers,
  Loader2,
  Play,
  RefreshCw,
  Search,
  UserX,
} from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { formatDateTime } from "@/utils/dateUtils";
import { LinkedInUploadDialog } from "@/components/linkedin/LinkedInUploadDialog";
import { LinkedInBatchCard } from "@/components/linkedin/LinkedInBatchCard";
import { LinkedInScrapeDialog } from "@/components/linkedin/LinkedInScrapeDialog";
import { LinkedInFilaRevisao } from "@/components/linkedin/LinkedInFilaRevisao";
import { LinkedInHistorico } from "@/components/linkedin/LinkedInHistorico";
import { LinkedInSinais } from "@/components/linkedin/LinkedInSinais";
import { LinkedInJobsTable } from "@/components/linkedin/LinkedInJobsTable";
import { LinkedInJobMatchModal } from "@/components/linkedin/LinkedInJobMatchModal";
import { linkedInApi } from "@/services/linkedin-api";
import { type LinkedInJob } from "@/types/linkedin";
import { toUserError } from "@/lib/api-error";

// Lista de freelancers filtrada por "LinkedIn inválido ou não encontrado".
const FREELANCERS_LINKEDIN_INVALIDO = `/freelancers?f=${encodeURIComponent(
  JSON.stringify({ external_links: ["linkedin_invalido"] }),
)}`;

const LOTES_POR_PAGINA = 15;

export default function LinkedInPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [matchJob, setMatchJob] = useState<LinkedInJob | null>(null);
  const [search, setSearch] = useState("");
  const [limiteLotes, setLimiteLotes] = useState(LOTES_POR_PAGINA);

  const buscaAtiva = search.trim().length > 0;

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ["linkedin-jobs"] });
    queryClient.invalidateQueries({ queryKey: ["linkedin-batches"] });
    queryClient.invalidateQueries({ queryKey: ["linkedin-resumo"] });
    queryClient.invalidateQueries({ queryKey: ["linkedin-scrape-preview"] });
  };

  const resumoQuery = useQuery({
    queryKey: ["linkedin-resumo"],
    queryFn: () => linkedInApi.resumo(),
    refetchInterval: (query) =>
      (query.state.data?.processando ?? 0) > 0 ? 2500 : false,
  });
  const resumo = resumoQuery.data;
  const processando = (resumo?.processando ?? 0) > 0;
  const pendentes =
    (resumo?.review_ready ?? 0) + (resumo?.awaiting_match ?? 0) + (resumo?.failed ?? 0);
  const resolvidos = (resumo?.aplicados ?? 0) + (resumo?.sem_mudanca ?? 0);
  const totalRevisavel = resolvidos + pendentes;
  const progresso =
    totalRevisavel > 0 ? Math.round((resolvidos / totalRevisavel) * 100) : 0;

  // Lotes: acompanhar uma leva específica (o que subiu ou o que a busca trouxe).
  const batchesQuery = useQuery({
    queryKey: ["linkedin-batches", limiteLotes],
    queryFn: () => linkedInApi.listBatches({ limit: limiteLotes }),
    refetchInterval: (query) =>
      (query.state.data?.batches ?? []).some((batch) => batch.processing > 0)
        ? 2500
        : false,
  });
  const batches = batchesQuery.data?.batches ?? [];
  const totalLotes = batchesQuery.data?.total ?? 0;

  const searchQuery = useQuery({
    queryKey: ["linkedin-jobs", "busca", search.trim()],
    queryFn: () => linkedInApi.listJobs({ q: search.trim(), limit: 100 }),
    enabled: buscaAtiva,
  });
  const resultados = searchQuery.data?.jobs ?? [];

  const pendingQuery = useQuery({
    queryKey: ["linkedin-jobs", "pending"],
    queryFn: () => linkedInApi.listJobs({ status: "pending", limit: 100 }),
  });
  const pendingJobs = (pendingQuery.data?.jobs ?? []).filter(
    (job) => job.source !== "scraping",
  );

  const processPending = useMutation({
    mutationFn: (jobIds: string[]) => linkedInApi.processBatch(jobIds),
    onSuccess: (result) => {
      const okCount = result.processed.length;
      const failCount = result.failed.length;
      toast({
        variant: failCount > 0 && okCount === 0 ? "destructive" : "default",
        title:
          failCount === 0
            ? "Processamento iniciado"
            : `${okCount} iniciados · ${failCount} falharam`,
        description:
          failCount === 0
            ? `${okCount} item(s) começaram a processar.`
            : result.failed
                .slice(0, 3)
                .map((f) => f.reason)
                .join("\n"),
      });
      invalidar();
    },
    onError: (e: Error) => {
      toast({
        title: "Erro ao iniciar processamento",
        description: toUserError(e).description,
        variant: "destructive",
      });
    },
  });

  const reprocess = useMutation({
    mutationFn: (job: LinkedInJob) => linkedInApi.reextract(job.id),
    onSuccess: () => {
      toast({ title: "Reprocessando", description: "A extração foi reiniciada." });
      invalidar();
    },
    onError: (e: Error) => {
      toast({
        title: "Erro ao reprocessar",
        description: toUserError(e).description,
        variant: "destructive",
      });
    },
  });

  const scrape = useMutation({
    mutationFn: ({ ids, limite }: { ids?: string[]; limite?: number }) =>
      linkedInApi.scrapeBatch(ids, limite),
    onSuccess: (result) => {
      toast({
        title:
          result.created === 0 ? "Nenhuma busca iniciada" : "Busca automática iniciada",
        description:
          result.created === 0
            ? "Nenhum freelancer elegível no momento."
            : `${result.created} perfil(is) na fila. Acompanhe na aba Lotes.`,
      });
      invalidar();
    },
    onError: (e: Error) => {
      toast({
        title: "Erro ao iniciar busca automática",
        description: toUserError(e).description,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="LinkedIn · Atualizações de perfil"
        subtitle="Atualize os dados de LinkedIn dos freelancers: suba PDFs ou busque automaticamente os perfis públicos. A IA extrai os dados e aplica ao perfil após sua revisão."
        actions={
          <div className="flex items-center gap-2">
            <LinkedInScrapeDialog
              iniciando={scrape.isPending}
              onIniciar={(limite) => scrape.mutate({ limite })}
            />
            <LinkedInUploadDialog onUploaded={invalidar} />
          </div>
        }
      />

      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
          <div className="space-y-1">
            <div className="text-sm">
              {processando ? (
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  Processando {resumo?.processando} perfil(is)...
                </span>
              ) : resumo?.ultima_atualizacao ? (
                <>
                  <span className="font-medium">Última atualização:</span>{" "}
                  {formatDateTime(resumo.ultima_atualizacao)}
                </>
              ) : (
                <span className="text-muted-foreground">Nenhum processamento ainda.</span>
              )}
            </div>
            {(resumo?.failed ?? 0) > 0 && (
              <div className="flex items-center gap-1.5 text-sm text-red-700">
                <AlertTriangle className="h-3.5 w-3.5" />
                {resumo?.failed} não puderam ser processados.
              </div>
            )}
            {(resumo?.aguardando_nova_busca ?? 0) > 0 && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                {resumo?.aguardando_nova_busca} esperando nova busca (a busca tenta de novo sozinha).
              </div>
            )}
            {(resumo?.nao_encontrados ?? 0) > 0 && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <UserX className="h-3.5 w-3.5" />
                <Link to={FREELANCERS_LINKEDIN_INVALIDO} className="underline underline-offset-2 hover:text-foreground">
                  {resumo?.nao_encontrados} perfis não encontrados no LinkedIn
                </Link>
              </div>
            )}
          </div>

          {totalRevisavel > 0 && (
            <div className="min-w-[260px] flex-1 space-y-1.5 sm:max-w-sm">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">
                  {pendentes === 0 ? "Tudo revisado" : `${pendentes} pra revisar`}
                </span>
                <span className="text-muted-foreground">
                  {resolvidos} de {totalRevisavel} resolvidos
                </span>
              </div>
              <Progress value={progresso} className="h-2" />
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, arquivo ou perfil..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        {pendingJobs.length > 0 && (
          <Button
            size="sm"
            onClick={() => processPending.mutate(pendingJobs.map((job) => job.id))}
            disabled={processPending.isPending}
            className="gap-1.5"
          >
            {processPending.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Iniciar processamento de pendentes ({pendingJobs.length})
          </Button>
        )}
      </div>

      {buscaAtiva ? (
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            Resultados da busca
          </h2>
          {searchQuery.isPending ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : resultados.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Nada encontrado para essa busca.
            </div>
          ) : (
            <div className="rounded-lg border bg-card">
              <LinkedInJobsTable
                jobs={resultados}
                onAssociate={(job) => setMatchJob(job)}
                onReview={(job) => navigate(`/linkedin/jobs/${job.id}/revisar`)}
                onReprocess={(job) => reprocess.mutate(job)}
                onRescrape={(job) =>
                  job.freelancer_id && scrape.mutate({ ids: [job.freelancer_id] })
                }
              />
            </div>
          )}
        </section>
      ) : (
        <Tabs defaultValue="pendencias">
          <TabsList>
            <TabsTrigger value="pendencias" className="gap-1.5">
              <Inbox className="h-4 w-4" />
              Pendências
              {pendentes > 0 && (
                <span className="rounded-full bg-primary/10 px-1.5 text-xs text-primary">
                  {pendentes}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="historico" className="gap-1.5">
              <History className="h-4 w-4" />
              Histórico
            </TabsTrigger>
            <TabsTrigger value="lotes" className="gap-1.5">
              <Layers className="h-4 w-4" />
              Lotes
            </TabsTrigger>
            <TabsTrigger value="sinais" className="gap-1.5">
              <Flame className="h-4 w-4" />
              Sinais
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pendencias" className="mt-4">
            <LinkedInFilaRevisao />
          </TabsContent>

          <TabsContent value="historico" className="mt-4">
            <LinkedInHistorico />
          </TabsContent>

          <TabsContent value="lotes" className="mt-4 space-y-3">
            {batchesQuery.isPending ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : batches.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Nenhum processamento ainda. Suba PDFs ou inicie uma busca automática.
              </div>
            ) : (
              <>
                {batches.map((batch) => (
                  <LinkedInBatchCard
                    key={batch.batch_id ?? "avulsos"}
                    batch={batch}
                    onReviewSequential={(batchId) =>
                      navigate(`/linkedin/lote/${batchId}/revisar`)
                    }
                  />
                ))}
                {totalLotes > batches.length && (
                  <div className="flex justify-center pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLimiteLotes((n) => n + LOTES_POR_PAGINA)}
                      disabled={batchesQuery.isFetching}
                    >
                      Mostrar mais lotes ({totalLotes - batches.length} restantes)
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="sinais" className="mt-4">
            <LinkedInSinais />
          </TabsContent>
        </Tabs>
      )}

      <LinkedInJobMatchModal job={matchJob} onClose={() => setMatchJob(null)} />
    </div>
  );
}
